import { Link } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight, Info } from "lucide-react";
import { PageHero, Container, SectionHead, CtaBand } from "@/components/site/blocks";
import { Reveal } from "@/components/site/motion";
import { useSeo } from "@/components/site/useSeo";

const INCLUDES = [
  "Documentary screening", "John's keynote", "JEM workshop", "Self-advocacy and employment-readiness discussion",
  "Goal-setting activity", "Q&A", "Reflection worksheet", "Facilitator guide", "Participant survey",
  "Feedback summary", "Limited pilot screening permission",
];
const PATH = [
  { t: "Start with a pilot", d: "Deliver one 3–4 hour session with your participants." },
  { t: "Review participant feedback", d: "Use the survey and feedback summary to see what worked." },
  { t: "Discuss expansion", d: "Decide together whether and how to grow." },
];
const OPTIONS = ["Screening package", "Workshop package", "Regional package", "Statewide package", "Annual partnership", "Employer or staff-training package"];

const PilotCard = () => (
  <Reveal>
    <article className="relative overflow-hidden rounded-[2rem] border border-[#F3C649]/50 bg-[#12141C] p-8 sm:p-12" data-testid="gvra-pilot-card">
      <div aria-hidden="true" className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#F3C649]/15 blur-3xl" />
      <div className="relative grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="label">Featured pilot</p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-[1.02] sm:text-5xl">JEM + GVRA Disability Empowerment Pilot</h2>
          <p className="mt-6 text-[#C8C5BB]">A proposed 3–4 hour program.</p>
          <div className="mt-10 border-t border-[#D4AF37]/25 pt-8">
            <p className="text-sm uppercase tracking-[0.2em] text-[#C8C5BB]">Proposed investment</p>
            <p className="mt-2 font-display text-7xl font-semibold text-[#F3C649] sm:text-8xl" data-testid="gvra-pilot-price">$3,500</p>
          </div>
          <ul className="mt-8 space-y-3 text-sm text-[#E6E3DA]">
            <li className="flex gap-3"><Info size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-[#F3C649]" />Final scope and contracting are subject to the agency's procurement process.</li>
            <li className="flex gap-3"><Info size={18} aria-hidden="true" className="mt-0.5 shrink-0 text-[#F3C649]" />Travel and accessible transportation are listed separately.</li>
          </ul>
        </div>
        <div className="lg:col-span-7">
          <h3 className="font-display text-2xl text-[#F3C649]">What the pilot includes</h3>
          <ul className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2" data-testid="gvra-pilot-includes">
            {INCLUDES.map((x) => (
              <li key={x} className="flex gap-3 border-b border-white/10 pb-4 text-[#F9F8F6]">
                <Check size={18} aria-hidden="true" className="mt-1 shrink-0 text-[#F3C649]" />{x}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  </Reveal>
);

const PathSteps = () => (
  <ol className="mt-14 grid gap-5 md:grid-cols-3" data-testid="pilot-path">
    {PATH.map((s, i) => (
      <Reveal key={s.t} delay={i * 0.12}>
        <li className="relative h-full rounded-3xl border border-[#D4AF37]/25 p-8">
          <span className="font-display text-6xl italic text-[#F3C649]">{i + 1}</span>
          <h3 className="mt-6 font-display text-3xl font-semibold">{s.t}</h3>
          <p className="mt-3 text-[#C8C5BB]">{s.d}</p>
          {i < 2 && <ArrowRight aria-hidden="true" className="absolute -right-4 top-1/2 hidden h-8 w-8 rounded-full bg-[#F3C649] p-1.5 text-[#0A0B0E] md:block" />}
        </li>
      </Reveal>
    ))}
  </ol>
);

export default function Partnerships() {
  useSeo("Partnerships & Pilot", "The proposed JEM + GVRA Disability Empowerment Pilot: a 3–4 hour program with a $3,500 proposed investment, plus other proposed partnership options.");
  return (
    <>
      <PageHero testid="partnerships-hero" label="Partnerships & Pilot" lines={["Start small.", <span key="g" className="italic text-[#F3C649]">Grow together.</span>]}
        intro="A focused pilot lets your organization see JEM in action, hear from participants, and then decide what comes next." />
      <section className="pb-24" data-testid="pilot-section"><Container><PilotCard /></Container></section>
      <section className="py-24 sm:py-28" data-testid="pilot-path-section">
        <Container>
          <SectionHead label="The path" title="Pilot → Feedback → Expansion" />
          <PathSteps />
        </Container>
      </section>
      <section className="bg-[#07080A] py-24 sm:py-32" data-testid="other-options-section">
        <Container>
          <SectionHead label="Other partnership options" title="Packages beyond the pilot."
            intro="These packages are separate from the $3,500 pilot. Each has its own scope and proposed pricing. Ask for current details." />
          <ul className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {OPTIONS.map((o, i) => (
              <Reveal key={o} delay={(i % 3) * 0.08}>
                <li className="flex h-full flex-col justify-between rounded-3xl border border-[#D4AF37]/20 bg-[#12141C] p-8" data-testid={`option-${i}`}>
                  <h3 className="font-display text-3xl font-semibold">{o}</h3>
                  <div className="mt-10">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#F3C649]">Proposed pricing</p>
                    <p className="mt-2 text-sm text-[#C8C5BB]">Scope and price available on request.</p>
                    <Link to="/contact" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F9F8F6] underline-offset-4 hover:text-[#F3C649] hover:underline" data-testid={`option-request-${i}`}>
                      Request current details <ArrowUpRight size={16} aria-hidden="true" />
                    </Link>
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </Container>
      </section>
      <CtaBand title="Ready to talk about a pilot?" />
    </>
  );
}
